import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { triggerExecutionsBulk } from './workflowExecutions.api';
import {
  invalidateAfterAutomationTrigger,
  type AutomationTriggerScope,
} from './invalidateAfterAutomationTrigger';

export type TriggerAutomationSilentlyInput = AutomationTriggerScope & {
  executionIds: number[];
};

// Fires `bulk_trigger` for pending executions WITHOUT showing an underway row.
//
// Used on stage RE-ENTRY (move out of a stage and back): the first activity
// already ran, so the popup must not re-show it — but the BE still created a
// fresh `pending` execution and only advances the rule once the FE triggers it.
// The worker then `already_created`-skips the step that ran and continues with
// the stage's remaining activities. Suppressing the popup must NOT suppress the
// trigger, or the rest of the stage's automation never runs.
//
// There is no undo window (nothing is shown to undo) and no row to report into,
// so failures are logged rather than surfaced.
export const useTriggerAutomationSilently = () => {
  const queryClient = useQueryClient();

  return useCallback(
    async ({
      executionIds,
      jobId,
      candidateIds,
    }: TriggerAutomationSilentlyInput): Promise<void> => {
      if (executionIds.length === 0) return;
      try {
        await triggerExecutionsBulk(executionIds);
        invalidateAfterAutomationTrigger(queryClient, { jobId, candidateIds });
      } catch (error) {
        console.error('useTriggerAutomationSilently', error);
      }
    },
    [queryClient]
  );
};
